/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePreloadedQuery } from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleSetCommunity } from 'src/components/TradeRule';
import TradeRuleSetCommunityPlaceholder from 'src/components/TradeRule/TradeRuleSetCommunity/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import buildTradeRuleSetCommunityRetryVariables from './helpers/buildTradeRuleSetCommunityRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'trade_rule.trade_rule_set_community_route.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the community trade rules. Please try again.',
  },
});

export const tradeRuleSetCommunityRouteQuery = graphql`
  query TradeRuleSetCommunityRoute_Query($uuid: ID!, $meterId: ID!, $now: Timestamp!) {
    property(uuid: $uuid) {
      ...TradeRuleSetCommunity_property
    }
    meter(uuid: $meterId) {
      ...TradeRuleSetCommunity_meter
    }
  }
`;

const routePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
      meterId: PropTypes.string,
    }),
  }),
});

/**
 * Renders the component from a preloaded retry query.
 * @param {object} props - Renderer props.
 * @param {object} props.routeProps - Found-relay route props.
 * @param {object} props.queryRef - Preloaded Relay query reference.
 * @returns {React.ReactNode} Route content.
 */
function TradeRuleSetCommunityPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRuleSetCommunityRouteQuery, queryRef);
  return <TradeRuleSetCommunity {...routeProps} {...queryData} />;
}

TradeRuleSetCommunityPreloadedQueryRenderer.propTypes = {
  routeProps: routePropsShape.isRequired,
  queryRef: PropTypes.shape({ dispose: PropTypes.func }).isRequired,
};

/**
 * Renders the community trade-rule route with retry and field-error handling.
 * @param {object} props - Renderer props.
 * @param {object} props.routeProps - Found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} Route content.
 */
function TradeRuleSetCommunityRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleSetCommunityRouteQuery,
    routeProps,
    {
      logLabel: 'TradeRuleSetCommunityRoute',
      getRetryVariablesFunc: buildTradeRuleSetCommunityRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TradeRuleSetCommunityPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleSetCommunityPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRuleSetCommunity {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleSetCommunityRouteRenderer.propTypes = {
  routeProps: routePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleSetCommunity route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleSetCommunity route content or loading state.
 */
export function tradeRuleSetCommunityRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleSetCommunityRouteRenderer
        key={`${routeProps.match.params.uuid}-${routeProps.match.params.meterId}`}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRuleSetCommunityRouteRenderer
      key={`${props.match.params.uuid}-${props.match.params.meterId}`}
      routeProps={props}
    />
  );
}

export default TradeRuleSetCommunityRouteRenderer;
